import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { ResponseNotificationService } from '../../services/response-notification/response-notification.service';

@Component({
  selector: 'app-calender-input',
  templateUrl: './calender-input.component.html',
  styleUrls: ['./calender-input.component.scss'],
})
export class CalenderInputComponent implements OnInit {

  @Input() minDate: Date | null = null; // Minimum date
  @Input() maxDate: Date | null = null; // Maximum date

  selectedDate: Date | null = null;

  currentYear!: number;
  currentMonth!: number;
  calendarDays: Date[] = [];

  readonly weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  readonly months = [
    { name: 'January', value: 0 },
    { name: 'February', value: 1 },
    { name: 'March', value: 2 },
    { name: 'April', value: 3 },
    { name: 'May', value: 4 },
    { name: 'June', value: 5 },
    { name: 'July', value: 6 },
    { name: 'August', value: 7 },
    { name: 'September', value: 8 },
    { name: 'October', value: 9 },
    { name: 'November', value: 10 },
    { name: 'December', value: 11 },
  ];
  years: number[] = [];

  @Output() change: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private _popoverController: PopoverController,
    private _responseNotificationService: ResponseNotificationService
  ) { }

  ngOnInit() {
    let date: Date;
    if (this.selectedDate) {
      date = new Date(this.selectedDate);
    } else if (this.minDate) {
      date = new Date(this.minDate);
    } else if (this.maxDate) {
      date = new Date(this.maxDate);
    } else {
      date = new Date();
    }
    this.currentMonth = date.getMonth();
    this.currentYear = date.getFullYear();
    this.generateYears();
    this.generateCalendar();
  }

  dismissComponent(){
    this._popoverController.dismiss();
  }

  selectDate(date: Date): void {
    if (date.getTime() !== 0 && this.isWithinRange(date)) {
      this.selectedDate = date;
      this.change.emit(this.selectedDate);
      this.dismissComponent();
    }
  }

  selectToday(): void {
    const today = new Date();
    if (this.isWithinRange(today)) {
      this.selectedDate = today;
      this.currentYear = today.getFullYear();
      this.currentMonth = today.getMonth();
      this.generateCalendar();
      this.change.emit(this.selectedDate);
      this.dismissComponent();
    } else {
      this._responseNotificationService.showMessage("Today is out of the allowed range", true);
    }
  }

  clearSelection(): void {
    this.selectedDate = null;
    this.change.emit(this.selectedDate);
    this.dismissComponent();
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return today.toDateString() === date.toDateString();
  }

  isSelectedDate(date: Date): boolean | null {
    return (
      this.selectedDate &&
      date.toDateString() === this.selectedDate.toDateString()
    );
  }

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    this.years = Array.from({ length: 201 }, (_, i) => currentYear - 100 + i);
  }

  generateCalendar(): void {
    this.calendarDays = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const startDay = (firstDay.getDay() + 6) % 7; // Adjust to start from Monday
    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();

    // Fill in days from the previous month
    const prevMonthDays = new Date(this.currentYear, this.currentMonth, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      this.calendarDays.push(new Date(this.currentYear, this.currentMonth - 1, prevMonthDays - i));
    }

    // Fill in actual days of the current month
    for (let i = 1; i <= daysInMonth; i++) {
      this.calendarDays.push(new Date(this.currentYear, this.currentMonth, i));
    }

    // Fill in days from the next month to complete the grid
    const remainingDays = 42 - this.calendarDays.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      this.calendarDays.push(new Date(this.currentYear, this.currentMonth + 1, i));
    }
  }

  navigateMonth(offset: number): void {
    this.currentMonth = Number(this.currentMonth) + offset;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  navigateYear(offset: number): void {
    this.currentYear = Number(this.currentYear) + offset;
    this.generateCalendar();
  }

  isPreviousYearDisabled(): boolean {
    return this.minDate ? this.currentYear <= this.minDate.getFullYear() : false;
  }

  isNextYearDisabled(): boolean {
    return this.maxDate ? this.currentYear >= this.maxDate.getFullYear() : false;
  }

  isPreviousMonthDisabled(): boolean {
    return this.minDate
      ? this.currentYear === this.minDate.getFullYear() && this.currentMonth <= this.minDate.getMonth()
      : false;
  }

  isNextMonthDisabled(): boolean {
    return this.maxDate
      ? this.currentYear === this.maxDate.getFullYear() && this.currentMonth >= this.maxDate.getMonth()
      : false;
  }

  isMonthDisabled(month: number): any {
    return (
      (this.minDate && this.currentYear === this.minDate.getFullYear() && month < this.minDate.getMonth()) ||
      (this.maxDate && this.currentYear === this.maxDate.getFullYear() && month > this.maxDate.getMonth())
    );
  }

  isYearDisabled(year: number): any {
    return (
      (this.minDate && year < this.minDate.getFullYear()) ||
      (this.maxDate && year > this.maxDate.getFullYear())
    );
  }

  isWithinRange(date: Date): boolean {
    return (
      (!this.minDate || this.removeTime(date) >= this.removeTime(this.minDate)) &&
      (!this.maxDate || this.removeTime(date) <= this.removeTime(this.maxDate))
    );
  }

  // Write a funtion to remove time from date and return the date
  removeTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  isDateDisabled(date: Date): boolean {
    if (!date || date.getTime() === 0) {
      return true;
    }
    const compareDate = this.removeTime(date);
    const min = this.minDate ? this.removeTime(this.minDate) : null;
    const max = this.maxDate ? this.removeTime(this.maxDate) : null;
    if (min && compareDate < min) {
      return true;
    }
    if (max && compareDate > max) {
      return true;
    }
    return false;
  }

  goToPreviousYear(): void {
    this.navigateYear(-1);
  }

  goToNextYear(): void {
    this.navigateYear(1);
  }

  goToPreviousMonth(): void {
    this.navigateMonth(-1);
  }

  goToNextMonth(): void {
    this.navigateMonth(1);
  }

}
